// CookingInProgress.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  keyframes,
  useTheme,
  Snackbar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Collapse,
  Chip,
  Card,
  CardMedia,
  CardContent,
  Grid,
  Avatar,
  LinearProgress,
  Tooltip,
  Stack,
} from "@mui/material";
import KitchenIcon from "@mui/icons-material/Kitchen";
import PanToolIcon from "@mui/icons-material/PanTool";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CategoryIcon from "@mui/icons-material/Category";
import TimerIcon from "@mui/icons-material/Timer";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import HeatPumpIcon from "@mui/icons-material/HeatPump";
import BlenderIcon from "@mui/icons-material/Blender";
import ErrorIcon from "@mui/icons-material/Error";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";

const floating = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(10deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const CookingInProgress = ({
  recipe,
  tasks = [],
  elapsedTime,
  onAbort,
  onReset,
  resetStatus,
  onNavigateBack,
}) => {
  const theme = useTheme();
  const [showPan, setShowPan] = useState(true);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [wakeLockSupported, setWakeLockSupported] = useState(false);
  const [wakeLockEnabled, setWakeLockEnabled] = useState(false);
  const [showWakeLockInfo, setShowWakeLockInfo] = useState(false);
  const [showTasks, setShowTasks] = useState(true);
  const [showAbortOptions, setShowAbortOptions] = useState(false);
  const [showRecipeInfo, setShowRecipeInfo] = useState(true);
  const [taskErrors, setTaskErrors] = useState([]);
  const [screenActivated, setScreenActivated] = useState(false);
  const [isAcquiringWakeLock, setIsAcquiringWakeLock] = useState(false);
  const wakeLock = useRef(null);
  const keepAliveInterval = useRef(null);
  const touchInterval = useRef(null);
  const wakeLockReleaseHandler = useRef(null);

  // Memoize validated tasks to prevent infinite loops
  const validatedTasks = useMemo(() => {
    return Array.isArray(tasks)
      ? tasks.map((task, index) => ({
          ...task,
          id: task.id || `task_${index}`,
          duration: Math.max(1, parseInt(task.duration) || 1), // Ensure minimum 1 second
          name: task.name || `Step ${index + 1}`,
          category: task.category || "Action",
          valid: task.valid !== false,
          complete: task.complete || 0,
          errorMessage: task.errorMessage || "",
          args: task.args || "",
          item_name: task.item_name || "",
          quantity: task.quantity || "",
          unit: task.unit || "",
          cookIn: task.cookIn || "",
        }))
      : [];
  }, [tasks]);

  // Parse recipe image safely
  const getRecipeImage = () => {
    try {
      if (recipe?.recipe_image) {
        const images = JSON.parse(recipe.recipe_image);
        return Array.isArray(images) ? images[0] : images;
      }
    } catch (error) {
      console.error("Error parsing recipe image:", error);
    }
    return "/placeholder-recipe.jpg";
  };

  // Calculate total recipe duration with error handling
  const totalDuration = useMemo(() => {
    try {
      if (recipe?.recipe_cooking_duration) {
        return parseInt(recipe.recipe_cooking_duration);
      }
      if (recipe?.cook_time) {
        return parseInt(recipe.cook_time);
      }
      // Fallback to sum of task durations
      return validatedTasks.reduce((total, task) => total + task.duration, 0);
    } catch (error) {
      console.error("Error calculating total duration:", error);
      return validatedTasks.reduce((total, task) => total + task.duration, 0);
    }
  }, [recipe, validatedTasks]);

  // Enhanced quotes based on recipe type
  const getContextualQuotes = () => {
    const recipeType = recipe?.recipe_type?.toLowerCase();
    const recipeName = recipe?.recipe_name || "your dish";

    const baseQuotes = [
      `Your delicious ${recipeName} is cooking to perfection...`,
      "The aromas are building beautifully...",
      "Each step brings us closer to culinary excellence...",
      "Good things take time - and this will be worth the wait...",
      "The magic of cooking is happening right now...",
      "Smart cooking technology at work for you...",
      "Every ingredient is finding its perfect place...",
      "Culinary artistry in motion - sit back and enjoy...",
      "The kitchen is working its magic for you...",
      "Flavors are dancing together in perfect harmony...",
      "Your taste buds are in for a delightful surprise...",
      "The symphony of cooking is playing just for you...",
      "Patience makes the heart grow fonder... and the food tastier...",
      "Love is the secret ingredient, and there's plenty in this dish...",
      "Great meals are worth the wait - yours is almost ready...",
      "The best recipes are made with time, care, and a touch of magic...",
      "Your kitchen is creating memories, one dish at a time...",
      "Cooking is an art, and you're about to taste a masterpiece...",
      "The anticipation makes every bite more satisfying...",
      "Traditional techniques meet modern convenience...",
      "Every great chef knows that timing is everything...",
      "The ingredients are transforming into something extraordinary...",
      "Slow and steady wins the race to deliciousness...",
      "Your meal is being crafted with precision and care...",
      "The fusion of flavors is reaching its peak...",
      "Cooking is therapy, and your dish is the cure...",
      "The kitchen gods are smiling upon your meal...",
      "Every bubble, every sizzle brings you closer to perfection...",
      "The alchemy of cooking is turning simple ingredients into gold...",
      "Your patience will be rewarded with incredible flavors...",
      "The art of slow cooking reveals nature's best secrets...",
      "Every second adds another layer of deliciousness...",
      "The temperature is just right, the timing is perfect...",
      "Your meal is being prepared with love and precision...",
      "The kitchen is orchestrating a flavor symphony...",
    ];

    const typeSpecificQuotes = {
      "non-veg": [
        "The proteins are tenderizing perfectly...",
        "Rich flavors are developing in your meat dish...",
        "Every minute adds depth to your hearty meal...",
        "The meat is absorbing all those wonderful spices...",
        "Protein perfection is just moments away...",
        "The marbling is melting into pure flavor...",
        "Your carnivorous cravings will soon be satisfied...",
        "The essence of the meat is being enhanced by every ingredient...",
        "Slow-cooked proteins develop the most incredible textures...",
        "The juices are locking in all that savory goodness...",
        "Each fiber is becoming more tender with time...",
        "The umami flavors are building to perfection...",
        "Your protein is reaching its optimal doneness...",
        "The natural flavors of the meat are being elevated...",
        "Carnivorous bliss is cooking to perfection...",
      ],
      veg: [
        "Fresh vegetables are releasing their natural flavors...",
        "Plant-based goodness is coming together beautifully...",
        "Healthy and delicious - the perfect combination...",
        "Nature's bounty is transforming into culinary art...",
        "The vegetables are retaining their vibrant colors and nutrients...",
        "Garden-fresh flavors are melding together harmoniously...",
        "Each vegetable is contributing its unique essence...",
        "Plant power is creating something truly special...",
        "The natural sweetness of vegetables is being enhanced...",
        "Fiber-rich goodness with incredible taste...",
        "The earth's gifts are being honored in this dish...",
        "Wholesome ingredients creating a nourishing masterpiece...",
        "The colors of the rainbow are dancing in your pot...",
        "Vegetarian cuisine at its absolute finest...",
        "Mother Nature's ingredients are working their magic...",
        "Pure, clean flavors are developing beautifully...",
        "The vitality of fresh produce shines through...",
      ],
      indian: [
        "Spices are dancing together in perfect harmony...",
        "Traditional flavors are melding magnificently...",
        "The essence of Indian cuisine is coming alive...",
        "Ancient recipes meet modern cooking precision...",
        "The warmth of Indian spices is filling the air...",
        "Turmeric, cumin, and coriander are working their magic...",
        "The soul of India is being captured in every bite...",
        "Generations of culinary wisdom in every spoonful...",
        "The heat is building layers of complex flavors...",
        "Aromatic spices are creating an olfactory journey...",
        "The richness of Indian tradition is cooking to perfection...",
        "Garam masala is adding its signature touch...",
        "The vibrant colors reflect the bold flavors within...",
        "Street food authenticity meets home-style comfort...",
        "The curry is reaching its perfect balance of spices...",
        "Regional Indian flavors are coming together beautifully...",
        "The tandoor-style aromas are filling your kitchen...",
        "Monsoon memories and festival flavors in every bite...",
        "The golden hour of Indian cooking is upon us...",
      ],
      chinese: [
        "The wok is working its ancient magic...",
        "Five-spice powder is creating aromatic wonder...",
        "The balance of sweet, sour, salty, and umami is perfect...",
        "Traditional Chinese techniques are shining through...",
        "Soy sauce and ginger are building complex layers...",
        "The art of Chinese cooking is being mastered...",
        "Steam is carrying the essence of the Orient...",
        "Centuries-old recipes are coming to life...",
        "The harmony of yin and yang in every ingredient...",
      ],
      italian: [
        "Italian passion is simmering in every drop...",
        "The Mediterranean sun is captured in these flavors...",
        "Herbs are releasing their aromatic oils beautifully...",
        "La dolce vita is cooking to perfection...",
        "Olive oil is adding its golden touch...",
        "The spirit of Italy is in every bubble...",
        "Fresh basil and oregano are working their magic...",
        "Nonna's secret techniques are being honored...",
      ],
      continental: [
        "European elegance meets refined cooking techniques...",
        "Classical flavors are developing sophisticated notes...",
        "The finesse of continental cuisine is emerging...",
        "Butter and herbs are creating luxurious textures...",
        "Refined techniques producing restaurant-quality results...",
        "The art of European cooking is on full display...",
      ],
      mexican: [
        "Fiesta flavors are coming together magnificently...",
        "The heat of chilies is balanced with cooling ingredients...",
        "Mexican soul food is warming your kitchen...",
        "Lime and cilantro are adding their fresh signature...",
        "The spirit of Mexico is alive in every spice...",
        "Vibrant colors reflect the bold Mexican flavors...",
      ],
      thai: [
        "The perfect balance of sweet, sour, salty, and spicy...",
        "Coconut milk is creating creamy perfection...",
        "Thai basil and lemongrass are adding aromatic magic...",
        "The complexity of Thai cuisine is unfolding...",
        "Southeast Asian flavors are dancing together...",
      ],
      japanese: [
        "The art of Japanese precision is at work...",
        "Umami flavors are reaching their perfect balance...",
        "Simplicity and elegance in every ingredient...",
        "The way of washoku is being honored...",
        "Fresh ingredients speak for themselves...",
        "Minimalist perfection is being achieved...",
      ],
      korean: [
        "The fire of Korean cuisine is building beautifully...",
        "Fermented flavors are adding incredible depth...",
        "Gochujang is working its spicy magic...",
        "The harmony of Korean banchan is emerging...",
        "Bold flavors with ancient traditions...",
      ],
      french: [
        "Classic French techniques are creating magic...",
        "The art of French cuisine is in every detail...",
        "Butter, wine, and herbs - the holy trinity...",
        "Sophisticated flavors worthy of a bistro...",
        "C'est magnifique - culinary artistry in motion...",
        "The essence of French cooking is being captured...",
      ],
      mediterranean: [
        "The sun-kissed flavors of the Mediterranean...",
        "Olive oil and fresh herbs are the stars...",
        "Healthy Mediterranean lifestyle in every bite...",
        "The azure coast is captured in these flavors...",
        "Fresh, vibrant, and utterly delicious...",
      ],
      "middle eastern": [
        "Ancient spice routes come alive in your kitchen...",
        "The warmth of Middle Eastern hospitality...",
        "Aromatic spices telling stories of old...",
        "The richness of Levantine cuisine unfolds...",
        "Cardamom, cinnamon, and rose water magic...",
      ],
      american: [
        "Classic American comfort food at its finest...",
        "The melting pot of flavors is simmering...",
        "All-American goodness cooking to perfection...",
        "From sea to shining sea - flavors unite...",
        "The taste of America in every bite...",
      ],
      vietnamese: [
        "Fresh herbs and vibrant flavors are combining...",
        "The soul of Vietnamese cuisine is awakening...",
        "Pho-nomenal flavors are developing...",
        "Fish sauce and lime are working their magic...",
        "The freshness of Vietnamese cooking shines...",
      ],
      fusion: [
        "East meets West in perfect harmony...",
        "Culinary boundaries are being beautifully blurred...",
        "Innovation meets tradition in every spoonful...",
        "The world's flavors are coming together...",
        "Creative cuisine at its most exciting...",
      ],
    };

    // Get quotes based on recipe category (cuisine type)
    const recipeCategory = recipe?.recipe_category?.toLowerCase() || "";
    const categoryQuotes = typeSpecificQuotes[recipeCategory] || [];

    // Get quotes based on recipe type (veg/non-veg)
    const specificQuotes = typeSpecificQuotes[recipeType] || [];

    return [...baseQuotes, ...specificQuotes, ...categoryQuotes];
  };

  const quotes = getContextualQuotes();

  // Calculate current task progress with enhanced logic
  const calculateTaskProgress = () => {
    if (!validatedTasks || validatedTasks.length === 0) {
      return { currentTaskIndex: 0, progress: 0, currentTask: null };
    }

    let totalElapsedTime = 0;
    let currentTaskIndex = 0;
    let taskProgress = 0;

    for (let i = 0; i < validatedTasks.length; i++) {
      const taskDuration = validatedTasks[i].duration;

      if (totalElapsedTime + taskDuration > elapsedTime) {
        currentTaskIndex = i;
        taskProgress = Math.min(
          ((elapsedTime - totalElapsedTime) / taskDuration) * 100,
          100
        );
        break;
      }

      totalElapsedTime += taskDuration;

      if (i === validatedTasks.length - 1) {
        currentTaskIndex = i;
        taskProgress = 100;
      }
    }

    return {
      currentTaskIndex,
      progress: Math.max(0, taskProgress),
      currentTask: validatedTasks[currentTaskIndex] || null,
    };
  };

  const { currentTaskIndex, progress, currentTask } = calculateTaskProgress();

  // Helper function to get pot display name
  const getPotDisplayName = (cookIn) => {
    switch (cookIn?.toLowerCase()) {
      case "np1":
        return "Pot 1";
      case "np2":
        return "Pot 2";
      default:
        return cookIn;
    }
  };

  // Enhanced category icon mapping
  const getCategoryIcon = (category) => {
    const categoryLower = category?.toLowerCase() || "";

    switch (true) {
      case categoryLower.includes("chimney"):
        return <KitchenIcon color="warning" />;
      case categoryLower.includes("nutripot"):
        return <DonutLargeIcon color="primary" />;
      case categoryLower.includes("dispenser"):
        return <BlenderIcon color="info" />;
      case categoryLower.includes("pour"):
        return <WaterDropIcon color="secondary" />;
      case categoryLower.includes("stir"):
        return <LocalDiningIcon color="success" />;
      case categoryLower.includes("waiting"):
        return <TimerIcon color="inherit" />;
      case categoryLower.includes("heat"):
        return <HeatPumpIcon color="error" />;
      default:
        return <PlayCircleIcon color="action" />;
    }
  };

  // Enhanced category color mapping
  const getCategoryColor = (category) => {
    const categoryLower = category?.toLowerCase() || "";

    switch (true) {
      case categoryLower.includes("chimney"):
        return theme.palette.warning.main;
      case categoryLower.includes("nutripot"):
        return theme.palette.primary.main;
      case categoryLower.includes("dispenser"):
        return theme.palette.info.main;
      case categoryLower.includes("pour"):
        return theme.palette.secondary.main;
      case categoryLower.includes("stir"):
        return theme.palette.success.main;
      case categoryLower.includes("waiting"):
        return theme.palette.grey[600];
      case categoryLower.includes("heat"):
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // Enhanced task description formatting
  const formatTaskDescription = (task) => {
    try {
      // Priority order: args > item_name + quantity + unit > name
      if (task.args && task.args.trim()) {
        return task.args;
      }

      if (task.item_name && task.quantity && task.unit) {
        return `${task.item_name} - ${task.quantity}${task.unit}`;
      }

      if (task.category === "Waiting Action" && task.quantity) {
        return formatWaitingTime(task.quantity);
      }

      return task.name || "Processing...";
    } catch (error) {
      console.error("Error formatting task description:", error);
      return task.name || "Processing...";
    }
  };

  // Format waiting time with better readability
  const formatWaitingTime = (seconds) => {
    try {
      const totalSeconds = parseInt(seconds) || 0;
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const remainingSeconds = totalSeconds % 60;

      if (hours > 0) {
        return `Wait ${hours}h ${minutes > 0 ? `${minutes}m` : ""}`;
      }
      if (minutes > 0) {
        return `Wait ${minutes}m ${
          remainingSeconds > 0 ? `${remainingSeconds}s` : ""
        }`;
      }
      return `Wait ${remainingSeconds}s`;
    } catch (error) {
      console.error("Error formatting waiting time:", error);
      return "Wait";
    }
  };

  // Enhanced time formatting
  const formatTime = (seconds) => {
    try {
      const totalSeconds = Math.max(0, parseInt(seconds) || 0);
      const hours = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;

      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, "0")}:${secs
          .toString()
          .padStart(2, "0")}`;
      }
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    } catch (error) {
      console.error("Error formatting time:", error);
      return "0:00";
    }
  };

  // Enhanced duration formatting
  const formatDuration = (seconds) => {
    try {
      const totalSeconds = Math.max(0, parseInt(seconds) || 0);

      if (totalSeconds === 0) {
        return "Instant";
      }

      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const remainingSeconds = totalSeconds % 60;

      if (hours > 0) {
        if (minutes > 0) {
          return `${hours}h ${minutes}m`;
        }
        return `${hours}h`;
      }

      if (minutes > 0) {
        if (remainingSeconds > 0 && minutes < 5) {
          // Show seconds only for short durations
          return `${minutes}m ${remainingSeconds}s`;
        }
        return `${minutes}m`;
      }

      return `${remainingSeconds}s`;
    } catch (error) {
      console.error("Error formatting duration:", error);
      return "1s";
    }
  };

  // Enhanced Wake Lock functionality with fallbacks
  const keepScreenAwake = async () => {
    // Prevent multiple simultaneous acquisition attempts
    if (isAcquiringWakeLock) {
      return false;
    }

    setIsAcquiringWakeLock(true);

    try {
      // Method 1: Wake Lock API (Modern browsers)
      if ("wakeLock" in navigator) {
        try {
          // Release existing wake lock if any
          if (wakeLock.current) {
            // Remove old event listener first
            if (wakeLockReleaseHandler.current) {
              wakeLock.current.removeEventListener(
                "release",
                wakeLockReleaseHandler.current
              );
            }
            await wakeLock.current.release();
            wakeLock.current = null;
          }

          // Request new wake lock
          wakeLock.current = await navigator.wakeLock.request("screen");
          setWakeLockEnabled(true);
          setWakeLockSupported(true);

          // Create single event listener
          wakeLockReleaseHandler.current = () => {
            setWakeLockEnabled(false);
            wakeLock.current = null;
            console.log("Wake Lock released");
            // Don't auto re-acquire here to prevent loop
            // Let the visibility/focus handlers manage re-acquisition
          };

          wakeLock.current.addEventListener(
            "release",
            wakeLockReleaseHandler.current
          );
          console.log("Wake Lock is active");
          setIsAcquiringWakeLock(false);
          return true;
        } catch (err) {
          console.error(`Wake Lock failed: ${err.message}`);
          setWakeLockEnabled(false);
        }
      }

      // Method 2: NoSleep.js fallback for older browsers/devices
      try {
        // Create invisible video element to prevent sleep
        if (!document.getElementById("nosleep-video")) {
          const noSleepVideo = document.createElement("video");
          noSleepVideo.id = "nosleep-video";
          noSleepVideo.setAttribute("title", "No Sleep");
          noSleepVideo.setAttribute("playsinline", "");
          noSleepVideo.setAttribute("muted", "");
          noSleepVideo.setAttribute("loop", "");
          noSleepVideo.style.position = "fixed";
          noSleepVideo.style.left = "-1000px";
          noSleepVideo.style.top = "-1000px";
          noSleepVideo.style.width = "1px";
          noSleepVideo.style.height = "1px";
          noSleepVideo.style.opacity = "0.01";
          noSleepVideo.style.pointerEvents = "none";

          // Add video source
          const source = document.createElement("source");
          source.src =
            "data:video/mp4;base64,AAAAIGZ0eXBtcDQyAAACAGlzb21pc28yYXZjMQAAAAhmdHlwbXA0MgAAAgBpc29taXNvMmF2YzEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABmXRyYWsAAABcdGtoZAAAAAMAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=";
          source.type = 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"';

          noSleepVideo.appendChild(source);
          document.body.appendChild(noSleepVideo);

          await noSleepVideo.play();
          console.log("NoSleep video fallback activated");
          setScreenActivated(true);
        }
      } catch (err) {
        console.error("NoSleep video fallback failed:", err);
      }

      // Method 3: Periodic interaction simulation (only if not already running)
      if (!keepAliveInterval.current) {
        keepAliveInterval.current = setInterval(() => {
          // Simulate user activity to prevent screen sleep
          const event = new Event("touchstart", { bubbles: true });
          document.dispatchEvent(event);

          // Also try focus events
          if (document.hidden) {
            window.focus();
          }
        }, 30000); // Every 30 seconds
      }

      // Method 4: Audio context keep-alive (silent) - only create once
      try {
        if (!window.audioContextKeepAlive) {
          const audioContext = new (window.AudioContext ||
            window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          gainNode.gain.value = 0; // Silent
          oscillator.frequency.value = 440;
          oscillator.start();

          // Keep audio context alive
          const audioInterval = setInterval(() => {
            if (audioContext.state === "suspended") {
              audioContext.resume();
            }
          }, 5000);

          // Store references to clean up later
          window.audioContextKeepAlive = {
            context: audioContext,
            interval: audioInterval,
          };

          console.log("Audio context keep-alive activated");
        }
      } catch (err) {
        console.error("Audio context keep-alive failed:", err);
      }

      setIsAcquiringWakeLock(false);
      return false;
    } catch (error) {
      console.error("Error in keepScreenAwake:", error);
      setIsAcquiringWakeLock(false);
      return false;
    }
  };

  // Release all wake lock methods
  const releaseScreenAwake = async () => {
    setIsAcquiringWakeLock(false);

    // Release Wake Lock API
    if (wakeLock.current) {
      try {
        // Remove event listener first
        if (wakeLockReleaseHandler.current) {
          wakeLock.current.removeEventListener(
            "release",
            wakeLockReleaseHandler.current
          );
          wakeLockReleaseHandler.current = null;
        }
        await wakeLock.current.release();
        wakeLock.current = null;
        setWakeLockEnabled(false);
        console.log("Wake Lock released");
      } catch (err) {
        console.error("Failed to release wake lock:", err);
      }
    }

    // Remove NoSleep video
    const noSleepVideo = document.getElementById("nosleep-video");
    if (noSleepVideo) {
      noSleepVideo.pause();
      noSleepVideo.remove();
      setScreenActivated(false);
      console.log("NoSleep video removed");
    }

    // Clear intervals
    if (keepAliveInterval.current) {
      clearInterval(keepAliveInterval.current);
      keepAliveInterval.current = null;
    }

    if (touchInterval.current) {
      clearInterval(touchInterval.current);
      touchInterval.current = null;
    }

    // Clean up audio context
    if (window.audioContextKeepAlive) {
      clearInterval(window.audioContextKeepAlive.interval);
      try {
        window.audioContextKeepAlive.context.close();
      } catch (err) {
        console.error("Error closing audio context:", err);
      }
      delete window.audioContextKeepAlive;
      console.log("Audio context keep-alive removed");
    }
  };

  // Debounced wake lock acquisition to prevent rapid calls
  const debouncedKeepScreenAwake = useRef(null);

  const requestWakeLock = () => {
    if (debouncedKeepScreenAwake.current) {
      clearTimeout(debouncedKeepScreenAwake.current);
    }

    debouncedKeepScreenAwake.current = setTimeout(() => {
      if (!wakeLockEnabled && !isAcquiringWakeLock) {
        keepScreenAwake();
      }
    }, 1000); // 1 second debounce
  };

  // Check wake lock support and initialize
  useEffect(() => {
    const initializeWakeLock = async () => {
      const success = await keepScreenAwake();
      if (!success) {
        setShowWakeLockInfo(true);
      }
    };

    initializeWakeLock();

    // Handle visibility changes with debouncing
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestWakeLock();
      }
    };

    // Handle page focus with debouncing
    const handleFocus = () => {
      requestWakeLock();
    };

    // Handle user interaction with debouncing
    const handleUserInteraction = () => {
      if (!wakeLockEnabled && !screenActivated && !isAcquiringWakeLock) {
        requestWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("click", handleUserInteraction);
    window.addEventListener("touchstart", handleUserInteraction);
    window.addEventListener("keypress", handleUserInteraction);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("click", handleUserInteraction);
      window.removeEventListener("touchstart", handleUserInteraction);
      window.removeEventListener("keypress", handleUserInteraction);

      // Clear debounce timeout
      if (debouncedKeepScreenAwake.current) {
        clearTimeout(debouncedKeepScreenAwake.current);
      }

      releaseScreenAwake();
    };
  }, [wakeLockEnabled, screenActivated, isAcquiringWakeLock]);

  // Quote rotation effect
  useEffect(() => {
    const timer = setInterval(() => {
      setQuoteIndex((prev) => (prev + 1) % quotes.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [quotes.length]);

  // Calculate overall progress
  const overallProgress =
    totalDuration > 0 ? Math.min((elapsedTime / totalDuration) * 100, 100) : 0;

  // Memoize task errors to prevent infinite loops
  const taskErrorsData = useMemo(() => {
    return validatedTasks
      .filter((task) => {
        // Skip tasks that are valid
        if (task.valid || !task.errorMessage) return false;

        // Skip "Ingredient not found in inventory" errors during cooking
        if (
          task.errorMessage
            .toLowerCase()
            .includes("ingredient not found in inventory")
        ) {
          return false;
        }

        return true;
      })
      .map((task) => ({
        taskName: task.name,
        error: task.errorMessage,
      }));
  }, [validatedTasks]);

  // Update task errors when taskErrorsData changes
  useEffect(() => {
    setTaskErrors(taskErrorsData);
  }, [taskErrorsData]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? `radial-gradient(circle at top, ${theme.palette.primary.dark}25 0%, ${theme.palette.background.default} 50%)`
            : `radial-gradient(circle at top, ${theme.palette.primary.light}15 0%, ${theme.palette.background.default} 50%)`,
        py: 4,
        px: 2,
      }}
    >
      <Grid container spacing={3} maxWidth="lg" sx={{ mx: "auto" }}>
        {/* Recipe Information Card */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={8}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              position: "sticky",
              top: 80,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.5)"
                  : "0 8px 32px rgba(0,0,0,0.12)",
            }}
          >
            {recipe?.recipe_image && (
              <CardMedia
                component="img"
                height="200"
                image={getRecipeImage()}
                alt={recipe?.recipe_name}
                sx={{
                  objectFit: "cover",
                  borderBottom: `4px solid ${theme.palette.primary.main}`,
                }}
                onError={(e) => {
                  e.target.src = "/placeholder-recipe.jpg";
                }}
              />
            )}
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                color="primary.main"
                sx={{
                  lineHeight: 1.2,
                  wordBreak: "break-word",
                }}
              >
                {recipe?.recipe_name || "Delicious Recipe"}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                {recipe?.recipe_type && (
                  <Chip
                    icon={<RestaurantIcon />}
                    label={recipe.recipe_type}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? `${theme.palette.secondary.main}20`
                          : "transparent",
                      borderColor: theme.palette.secondary.main,
                    }}
                  />
                )}
                {recipe?.recipe_category && (
                  <Chip
                    icon={<CategoryIcon />}
                    label={recipe.recipe_category}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? `${theme.palette.primary.main}20`
                          : "transparent",
                      borderColor: theme.palette.primary.main,
                    }}
                  />
                )}
                {recipe?.merged_recipe_names?.length > 1 && (
                  <Chip
                    label="Merged Recipe"
                    size="small"
                    color="info"
                    variant="filled"
                    sx={{
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? theme.palette.info.dark
                          : theme.palette.info.main,
                      color: theme.palette.info.contrastText,
                    }}
                  />
                )}
              </Box>

              <Divider sx={{ my: 2, bgcolor: theme.palette.divider }} />

              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AccessTimeIcon
                    sx={{ fontSize: 20, mr: 1, color: "primary.main" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Total Time:</strong> {formatDuration(totalDuration)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <TimerIcon
                    sx={{ fontSize: 20, mr: 1, color: "secondary.main" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Elapsed:</strong> {formatTime(elapsedTime)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon
                    sx={{ fontSize: 20, mr: 1, color: "success.main" }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Steps:</strong> {currentTaskIndex + 1} of{" "}
                    {validatedTasks.length}
                  </Typography>
                </Box>

                {/* Overall Progress */}
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    gutterBottom
                    color="text.primary"
                  >
                    Overall Progress
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={overallProgress}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      mb: 1,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[800]
                          : theme.palette.grey[200],
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 5,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(overallProgress)}% Complete
                  </Typography>
                </Box>

                {/* Error Display */}
                {taskErrors.length > 0 && (
                  <Alert
                    severity="warning"
                    icon={<ErrorIcon />}
                    sx={{
                      mt: 2,
                      fontSize: "0.875rem",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? `${theme.palette.warning.dark}20`
                          : `${theme.palette.warning.light}40`,
                      border: `1px solid ${theme.palette.warning.main}`,
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.warning.light
                          : theme.palette.warning.dark,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      gutterBottom
                    >
                      Task Validation Issues
                    </Typography>
                    {taskErrors.slice(0, 2).map((error, index) => (
                      <Typography key={index} variant="caption" display="block">
                        • {error.taskName}: {error.error}
                      </Typography>
                    ))}
                    {taskErrors.length > 2 && (
                      <Typography variant="caption" color="text.secondary">
                        ... and {taskErrors.length - 2} more issues
                      </Typography>
                    )}
                  </Alert>
                )}

                {/* Control Buttons */}
                <Box sx={{ mt: 3 }}>
                  {!showAbortOptions ? (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => setShowAbortOptions(true)}
                      startIcon={<PanToolIcon />}
                      size="large"
                      fullWidth
                      sx={{
                        py: 2,
                        borderRadius: 3,
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 4px 16px rgba(244, 67, 54, 0.3)"
                            : 4,
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow:
                            theme.palette.mode === "dark"
                              ? "0 6px 20px rgba(244, 67, 54, 0.4)"
                              : 6,
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Emergency Stop
                    </Button>
                  ) : (
                    <Stack spacing={2}>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={onAbort}
                        startIcon={<PanToolIcon />}
                        size="large"
                        fullWidth
                        sx={{ py: 1.5, borderRadius: 3 }}
                      >
                        Confirm Stop
                      </Button>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => setShowAbortOptions(false)}
                          startIcon={<ArrowBackIcon />}
                          size="medium"
                          sx={{
                            flex: 1,
                            borderRadius: 3,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            "&:hover": {
                              borderColor: theme.palette.primary.dark,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? `${theme.palette.primary.main}10`
                                  : `${theme.palette.primary.main}08`,
                            },
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={onNavigateBack}
                          startIcon={<ArrowBackIcon />}
                          size="medium"
                          sx={{
                            flex: 1,
                            borderRadius: 3,
                            borderColor: theme.palette.secondary.main,
                            color: theme.palette.secondary.main,
                            "&:hover": {
                              borderColor: theme.palette.secondary.dark,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? `${theme.palette.secondary.main}10`
                                  : `${theme.palette.secondary.main}08`,
                            },
                          }}
                        >
                          Return
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </Box>

                {/* System Controls */}
                <Box>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onReset}
                    disabled={resetStatus.loading}
                    size="large"
                    fullWidth
                    sx={{
                      py: 2,
                      borderRadius: 3,
                      borderWidth: 2,
                      "&:hover": { borderWidth: 2 },
                      "&:disabled": { opacity: 0.7 },
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.main,
                    }}
                  >
                    {resetStatus.loading ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        Resetting...
                      </>
                    ) : (
                      "Reset System"
                    )}
                  </Button>
                </Box>

                {/* Enhanced Status Indicators */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {/* Wake Lock Status */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: wakeLockEnabled
                        ? theme.palette.mode === "dark"
                          ? `${theme.palette.success.dark}30`
                          : `${theme.palette.success.light}20`
                        : theme.palette.mode === "dark"
                        ? `${theme.palette.warning.dark}30`
                        : `${theme.palette.warning.light}20`,
                      border: `1px solid ${
                        wakeLockEnabled
                          ? theme.palette.success.main
                          : theme.palette.warning.main
                      }`,
                    }}
                  >
                    <VisibilityIcon
                      fontSize="small"
                      color={wakeLockEnabled ? "success" : "warning"}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {wakeLockEnabled
                        ? "Screen Lock: Active"
                        : "Screen Lock: Fallback Mode"}
                    </Typography>
                  </Box>

                  {/* Additional Screen Keep-Awake Status */}
                  {screenActivated && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 2,
                        borderRadius: 3,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? `${theme.palette.info.dark}30`
                            : `${theme.palette.info.light}20`,
                        border: `1px solid ${theme.palette.info.main}`,
                      }}
                    >
                      <PlayCircleIcon fontSize="small" color="info" />
                      <Typography variant="body2" color="text.secondary">
                        Keep-Awake: Video Fallback Active
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Error Display */}
                {resetStatus.error && (
                  <Alert
                    severity="error"
                    sx={{
                      borderRadius: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? `${theme.palette.error.dark}20`
                          : `${theme.palette.error.light}40`,
                      border: `1px solid ${theme.palette.error.main}`,
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.error.light
                          : theme.palette.error.dark,
                    }}
                  >
                    Reset failed: {resetStatus.error}
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Main Cooking Display */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              mb: 3,
            }}
          >
            {/* Animated Cooking Icon */}
            <Box
              sx={{
                position: "relative",
                width: { xs: 160, sm: 200 },
                height: { xs: 160, sm: 200 },
                mb: 3,
                animation: `${floating} 4s ease-in-out infinite`,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  background:
                    theme.palette.mode === "dark"
                      ? `linear-gradient(45deg, ${theme.palette.primary.dark}40 0%, ${theme.palette.secondary.dark}40 100%)`
                      : `linear-gradient(45deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}20 100%)`,
                  animation: `${pulse} 3s ease-in-out infinite`,
                  filter:
                    theme.palette.mode === "dark"
                      ? "drop-shadow(0 0 20px rgba(25, 118, 210, 0.3))"
                      : "none",
                }}
              />
              <KitchenIcon
                sx={{
                  position: "absolute",
                  fontSize: { xs: 100, sm: 120 },
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.primary.light
                      : theme.palette.primary.main,
                  opacity: theme.palette.mode === "dark" ? 0.4 : 0.3,
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
              {currentTask && (
                <Avatar
                  sx={{
                    position: "absolute",
                    fontSize: { xs: 60, sm: 80 },
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                    bgcolor: getCategoryColor(currentTask.category),
                    color: "white",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    animation: `${spin} 4s linear infinite`,
                    filter:
                      theme.palette.mode === "dark"
                        ? `drop-shadow(0 4px 12px rgba(0,0,0,0.6))`
                        : `drop-shadow(0 4px 8px rgba(0,0,0,0.1))`,
                    border: `3px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(255,255,255,0.8)"
                    }`,
                  }}
                >
                  {getCategoryIcon(currentTask.category)}
                </Avatar>
              )}
            </Box>

            {/* Timer Display */}
            <Typography
              variant="h1"
              sx={{
                mb: 2,
                fontWeight: 800,
                fontSize: { xs: "2.5rem", sm: "4rem", md: "5rem" },
                background:
                  theme.palette.mode === "dark"
                    ? `linear-gradient(45deg, ${theme.palette.primary.light} 30%, ${theme.palette.secondary.light} 90%)`
                    : `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "0.1em",
                fontFamily: "monospace",
                filter:
                  theme.palette.mode === "dark"
                    ? "drop-shadow(0 2px 4px rgba(0,0,0,0.8))"
                    : "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
              }}
            >
              {formatTime(elapsedTime)}
            </Typography>

            {/* Current Task Display */}
            {currentTask && (
              <Box sx={{ mb: 3, maxWidth: 600, px: 2 }}>
                <Chip
                  label={`Step ${currentTaskIndex + 1}`}
                  color="primary"
                  size="small"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.dark
                        : theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  {currentTask.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {formatTaskDescription(currentTask)}
                </Typography>
                {currentTask.cookIn && (
                  <Chip
                    label={`Cooking in: ${getPotDisplayName(
                      currentTask.cookIn
                    )}`}
                    color="info"
                    size="small"
                    sx={{
                      mr: 1,
                      mb: 1,
                      fontWeight: "bold",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? `${theme.palette.info.dark}60`
                          : `${theme.palette.info.light}40`,
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.info.light
                          : theme.palette.info.dark,
                      border: `1px solid ${theme.palette.info.main}`,
                    }}
                  />
                )}
                {currentTask.duration > 1 && (
                  <Typography variant="caption" color="text.secondary">
                    Duration: {formatDuration(currentTask.duration)}
                  </Typography>
                )}
              </Box>
            )}

            {/* Contextual Quote */}
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                maxWidth: 600,
                px: 2,
                transition: "opacity 1.5s ease",
                opacity: 0.9,
                fontStyle: "italic",
                color: "text.secondary",
                fontSize: { xs: "1rem", sm: "1.25rem" },
                textShadow:
                  theme.palette.mode === "dark"
                    ? "0 1px 2px rgba(0,0,0,0.8)"
                    : "none",
              }}
            >
              {quotes[quoteIndex]}
            </Typography>
          </Box>

          {/* Task Progress Section */}
          <Paper
            elevation={8}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              mb: 4,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.5)"
                  : "0 8px 32px rgba(0,0,0,0.12)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 3,
                background:
                  theme.palette.mode === "dark"
                    ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: theme.palette.primary.contrastText,
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    'url(\'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="white" opacity="0.1"/></svg>\') repeat',
                  backgroundSize: "20px 20px",
                },
              }}
              onClick={() => setShowTasks(!showTasks)}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  zIndex: 1,
                }}
              >
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.15)"
                        : "rgba(255,255,255,0.2)",
                    animation: `${pulse} 2s ease-in-out infinite`,
                    border: `2px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(255,255,255,0.3)"
                    }`,
                  }}
                >
                  {currentTask && getCategoryIcon(currentTask.category)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Cooking Steps Progress
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {validatedTasks.length > 0
                      ? `Step ${currentTaskIndex + 1} of ${
                          validatedTasks.length
                        }`
                      : "Preparing..."}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  zIndex: 1,
                }}
              >
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="h5" fontWeight="bold">
                    {Math.round(progress)}%
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Current Step
                  </Typography>
                </Box>
                {showTasks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Box>
            </Box>

            <Collapse in={showTasks}>
              {/* Current Task Highlight */}
              {currentTask && (
                <Box
                  sx={{
                    p: 3,
                    bgcolor: `${getCategoryColor(currentTask.category)}15`,
                    borderBottom: `3px solid ${getCategoryColor(
                      currentTask.category
                    )}`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: getCategoryColor(currentTask.category),
                        color: "white",
                        animation: `${pulse} 1.5s ease-in-out infinite`,
                      }}
                    >
                      {getCategoryIcon(currentTask.category)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="text.primary"
                      >
                        Currently: {currentTask.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatTaskDescription(currentTask)}
                      </Typography>
                      {currentTask.cookIn && (
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={`Cooking in: ${getPotDisplayName(
                              currentTask.cookIn
                            )}`}
                            color="info"
                            size="small"
                            sx={{
                              fontWeight: "bold",
                              bgcolor: theme.palette.info.light + "40",
                              color: theme.palette.info.dark,
                            }}
                          />
                        </Box>
                      )}
                      {currentTask.errorMessage && !currentTask.valid && (
                        <Alert
                          severity="warning"
                          sx={{ mt: 1, fontSize: "0.75rem" }}
                          icon={<ErrorIcon fontSize="small" />}
                        >
                          {currentTask.errorMessage}
                        </Alert>
                      )}
                    </Box>
                    <Stack alignItems="flex-end" spacing={0.5}>
                      <Chip
                        label="ACTIVE"
                        color="primary"
                        size="small"
                        sx={{
                          fontWeight: "bold",
                          animation: `${pulse} 2s ease-in-out infinite`,
                        }}
                      />
                      {currentTask.category && (
                        <Chip
                          label={currentTask.category}
                          size="small"
                          sx={{
                            bgcolor: `${getCategoryColor(
                              currentTask.category
                            )}20`,
                            color: getCategoryColor(currentTask.category),
                            fontSize: "0.7rem",
                          }}
                        />
                      )}
                    </Stack>
                  </Box>

                  {/* Enhanced Progress Bar */}
                  <Box sx={{ width: "100%", mb: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Step Progress
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {Math.round(progress)}%
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: "100%",
                        height: 12,
                        borderRadius: 6,
                        bgcolor: "rgba(0,0,0,0.1)",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${progress}%`,
                          height: "100%",
                          background: `linear-gradient(90deg, ${getCategoryColor(
                            currentTask.category
                          )} 0%, ${getCategoryColor(
                            currentTask.category
                          )}AA 100%)`,
                          borderRadius: 6,
                          transition: "width 1s ease",
                          position: "relative",
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background:
                              "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)",
                            animation: `${shimmer} 2s infinite`,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Tasks List */}
              <List sx={{ py: 0, maxHeight: 400, overflow: "auto" }}>
                {validatedTasks.map((task, idx) => {
                  const isCompleted = idx < currentTaskIndex;
                  const isCurrent = idx === currentTaskIndex;
                  const isUpcoming = idx > currentTaskIndex;

                  return (
                    <Box key={task.id || idx}>
                      <ListItem
                        sx={{
                          py: 2,
                          px: 3,
                          bgcolor: isCurrent
                            ? `${getCategoryColor(task.category)}08`
                            : isCompleted
                            ? `${theme.palette.success.light}08`
                            : "transparent",
                          borderLeft: isCurrent
                            ? `4px solid ${getCategoryColor(task.category)}`
                            : isCompleted
                            ? `4px solid ${theme.palette.success.main}`
                            : `4px solid ${theme.palette.grey[300]}`,
                          transition: "all 0.3s ease",
                          opacity: isUpcoming ? 0.6 : 1,
                          transform: isCurrent
                            ? "translateX(4px)"
                            : "translateX(0)",
                        }}
                      >
                        <ListItemIcon>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: isCompleted
                                ? theme.palette.success.main
                                : isCurrent
                                ? getCategoryColor(task.category)
                                : theme.palette.grey[300],
                              color: "white",
                              fontSize: "0.875rem",
                              fontWeight: "bold",
                              animation: isCurrent
                                ? `${pulse} 2s ease-in-out infinite`
                                : "none",
                            }}
                          >
                            {isCompleted ? (
                              <CheckCircleIcon fontSize="small" />
                            ) : isCurrent ? (
                              <AutorenewIcon
                                fontSize="small"
                                sx={{
                                  animation: `${spin} 2s linear infinite`,
                                }}
                              />
                            ) : (
                              idx + 1
                            )}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body1"
                              fontWeight={isCurrent ? "bold" : "normal"}
                              color={
                                isCompleted
                                  ? "success.main"
                                  : isCurrent
                                  ? "primary.main"
                                  : "text.primary"
                              }
                            >
                              {task.name}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {formatTaskDescription(task)}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  mt: 0.5,
                                  flexWrap: "wrap",
                                }}
                              >
                                {task.cookIn && (
                                  <Chip
                                    label={`${getPotDisplayName(task.cookIn)}`}
                                    color="info"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: "0.65rem",
                                      fontWeight: "bold",
                                      bgcolor: theme.palette.info.light + "30",
                                      color: theme.palette.info.dark,
                                    }}
                                  />
                                )}
                                {task.duration && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Duration: {formatDuration(task.duration)}
                                  </Typography>
                                )}
                                {!task.valid && (
                                  <Typography
                                    variant="caption"
                                    color="error.main"
                                    sx={{ fontWeight: "bold" }}
                                  >
                                    ⚠ Validation Issue
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          }
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 0.5,
                          }}
                        >
                          {task.category && (
                            <Chip
                              label={task.category}
                              size="small"
                              sx={{
                                bgcolor: `${getCategoryColor(task.category)}20`,
                                color: getCategoryColor(task.category),
                                fontWeight: "bold",
                                fontSize: "0.7rem",
                              }}
                            />
                          )}
                          {isCurrent && (
                            <Chip
                              label="ACTIVE"
                              color="primary"
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: "bold",
                                animation: `${pulse} 2s ease-in-out infinite`,
                              }}
                            />
                          )}
                          {isCompleted && (
                            <Chip
                              label="COMPLETED"
                              color="success"
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: "bold" }}
                            />
                          )}
                        </Box>
                      </ListItem>
                      {idx < validatedTasks.length - 1 && (
                        <Divider variant="inset" component="li" />
                      )}
                    </Box>
                  );
                })}
              </List>
            </Collapse>
          </Paper>
        </Grid>
      </Grid>

      {/* Enhanced Wake Lock Info Snackbar */}
      <Snackbar
        open={showWakeLockInfo}
        autoHideDuration={15000}
        onClose={() => setShowWakeLockInfo(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          maxWidth: "90%",
          "& .MuiSnackbarContent-root": {
            bgcolor:
              theme.palette.mode === "dark"
                ? theme.palette.warning.dark
                : theme.palette.warning.light,
            color:
              theme.palette.mode === "dark"
                ? theme.palette.warning.contrastText
                : theme.palette.warning.dark,
            borderRadius: 3,
            fontSize: "0.9rem",
            padding: "12px 16px",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0,0,0,0.6)"
                : "0 4px 20px rgba(0,0,0,0.15)",
            border: `1px solid ${theme.palette.warning.main}`,
          },
        }}
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ mr: 1 }}>
              {wakeLockEnabled || screenActivated ? "✓ Active" : "⚠ Fallback"}
            </Typography>
            <IconButton
              size="small"
              color="inherit"
              onClick={() => setShowWakeLockInfo(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <Box>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Screen Keep-Awake Status
          </Typography>
          <Typography variant="body2">
            {wakeLockEnabled
              ? "✓ Screen will stay awake during cooking"
              : screenActivated
              ? "✓ Fallback method active - screen should stay awake"
              : "⚠ Please tap screen occasionally to prevent sleep mode"}
          </Typography>
          {!wakeLockEnabled && !screenActivated && (
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 1, opacity: 0.8 }}
            >
              Your device may not support automatic screen wake-lock. We're
              using fallback methods to keep your screen active.
            </Typography>
          )}
        </Box>
      </Snackbar>
    </Box>
  );
};
